"use client";

import { useEffect, useMemo, useCallback, useState, useRef } from "react";
import {
  useCreateBlockNote,
  SideMenu as BlockSideMenu,
  SideMenuController,
  DragHandleButton,
  SuggestionMenuController,
  getDefaultReactSlashMenuItems,
  LinkToolbarController,
  FormattingToolbarController,
  useBlockNoteEditor,
  useComponentsContext,
  EditLinkButton,
  OpenLinkButton,
  DeleteLinkButton,
} from "@blocknote/react";
import { BlockNoteView } from "@blocknote/mantine";
import { insertOrUpdateBlock } from "@blocknote/core";
import { ko, en } from "@blocknote/core/locales";
import { cn } from "../utils/cn";

import type { DefaultPartialBlock, LumirEditorProps } from "../types";

import { createS3Uploader } from "../utils/s3-uploader";
import { schema } from "../blocks/HtmlPreview";
import { FloatingMenu } from "./FloatingMenu";
import { LumirEditorError } from "../errors/LumirEditorError";
import { VerticalAlignmentExtension } from "../extensions/VerticalAlignmentExtension";
import { RowHeightExtension } from "../extensions/RowHeightExtension";
import { TableAlignmentExtension } from "../extensions/TableAlignmentExtension";
import { TableSelectAllExtension } from "../extensions/TableSelectAllExtension";
import { InactiveSelectionExtension } from "../extensions/InactiveSelectionExtension";
import { insertTwoColumns } from "../blocks/columns/insertColumns";
import { CustomFormattingToolbar } from "./CustomFormattingToolbar";
import { LumirDragHandleMenu } from "./LumirDragHandleMenu";
import { LumirTableHandlesController } from "./LumirTableHandlesController";
import {
  injectTableCellAttrs,
  injectTableBlockAttrs,
} from "../utils/table-vertical-alignment";
import {
  liftFontSize,
  lowerFontSize,
} from "../utils/font-size-serialization";
import { isInTableCell } from "../utils/prosemirror-table-utils";
import { removeFocusedRowOrColumn } from "../utils/table-delete";
import { normalizeExcelTableHtml } from "../utils/excel-paste";
import {
  computeFittedColumnWidthsPerTable,
  collectTableBlocks,
  applyFittedWidthsToNewTables,
} from "../utils/table-paste-fit";
import {
  MAX_FILE_SIZE,
  MAX_VIDEO_FILE_SIZE,
  BLOCKED_EXTENSIONS,
  ALLOWED_VIDEO_MIME_TYPES,
  ALLOWED_VIDEO_EXTENSIONS,
} from "../constants/limits";

// #region agent log
const DEBUG_LOG = (loc: string, msg: string, data: Record<string, unknown>) => {
  const p = fetch("http://127.0.0.1:7686/ingest/1f8ee1c5-0cf0-4ae7-91ed-5ea7ed17130a", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "b73262" },
    body: JSON.stringify({
      sessionId: "b73262",
      location: loc,
      message: msg,
      data,
      timestamp: Date.now(),
    }),
  });
  if (p && typeof (p as Promise<unknown>).catch === "function") (p as Promise<unknown>).catch(() => {});
};
// #endregion

// ==========================================
// 유틸리티 클래스들
// ==========================================

/**
 * 콘텐츠 관리 유틸리티
 * 기본 블록 생성 및 콘텐츠 검증 로직을 담당
 */
export class ContentUtils {
  /**
   * JSON 문자열의 유효성을 검증합니다
   * @param jsonString 검증할 JSON 문자열
   * @returns 유효한 JSON 문자열인지 여부
   */
  static isValidJSONString(jsonString: string): boolean {
    try {
      const parsed = JSON.parse(jsonString);
      return Array.isArray(parsed);
    } catch {
      return false;
    }
  }

  /**
   * JSON 문자열을 DefaultPartialBlock 배열로 파싱합니다
   * @param jsonString JSON 문자열
   * @returns 파싱된 블록 배열 또는 null (파싱 실패 시)
   */
  static parseJSONContent(jsonString: string): DefaultPartialBlock[] | null {
    try {
      const parsed = JSON.parse(jsonString);
      if (Array.isArray(parsed)) {
        return parsed as DefaultPartialBlock[];
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * 기본 paragraph 블록 생성
   * @returns 기본 설정이 적용된 DefaultPartialBlock
   */
  static createDefaultBlock(): DefaultPartialBlock {
    return {
      type: "paragraph",
      props: {
        textColor: "default",
        backgroundColor: "default",
        textAlignment: "left",
      },
      content: [{ type: "text", text: "", styles: {} }],
      children: [],
    };
  }

  /**
   * 콘텐츠 유효성 검증 및 기본값 설정
   * @param content 사용자 제공 콘텐츠 (객체 배열 또는 JSON 문자열)
   * @param emptyBlockCount 빈 블록 개수 (기본값: 3)
   * @returns 검증된 콘텐츠 배열
   */
  static validateContent(
    content?: DefaultPartialBlock[] | string,
    emptyBlockCount: number = 3
  ): DefaultPartialBlock[] {
    // 1. 문자열인 경우 JSON 파싱 시도
    if (typeof content === "string") {
      if (content.trim() === "") {
        return this.createEmptyBlocks(emptyBlockCount);
      }

      const parsedContent = this.parseJSONContent(content);
      if (parsedContent && parsedContent.length > 0) {
        return parsedContent;
      }

      // 파싱 실패 시 빈 블록 생성
      return this.createEmptyBlocks(emptyBlockCount);
    }

    // 2. 배열인 경우 기존 로직
    if (!content || content.length === 0) {
      return this.createEmptyBlocks(emptyBlockCount);
    }

    return content;
  }

  /**
   * 빈 블록들을 생성합니다
   * @param emptyBlockCount 생성할 블록 개수
   * @returns 생성된 빈 블록 배열
   */
  private static createEmptyBlocks(
    emptyBlockCount: number
  ): DefaultPartialBlock[] {
    return Array.from({ length: emptyBlockCount }, () =>
      this.createDefaultBlock()
    );
  }
}

/**
 * 에디터 설정 관리 유틸리티
 * 각종 설정의 기본값과 검증 로직을 담당
 */
export class EditorConfig {
  /**
   * 테이블 설정 기본값 적용
   * @param userTables 사용자 테이블 설정
   * @returns 기본값이 적용된 테이블 설정
   */
  static getDefaultTableConfig(userTables?: LumirEditorProps["tables"]) {
    return {
      splitCells: userTables?.splitCells ?? true,
      cellBackgroundColor: userTables?.cellBackgroundColor ?? true,
      cellTextColor: userTables?.cellTextColor ?? true,
      headers: userTables?.headers ?? true,
    };
  }

  /**
   * 헤딩 설정 기본값 적용
   * @param userHeading 사용자 헤딩 설정
   * @returns 기본값이 적용된 헤딩 설정
   */
  static getDefaultHeadingConfig(userHeading?: LumirEditorProps["heading"]) {
    return userHeading?.levels && userHeading.levels.length > 0
      ? userHeading
      : { levels: [1, 2, 3, 4, 5, 6] as (1 | 2 | 3 | 4 | 5 | 6)[] };
  }

  /**
   * 비활성화할 확장 기능 목록 생성
   * @param userExtensions 사용자 정의 비활성 확장
   * @param allowVideo 비디오 업로드 허용 여부
   * @param allowAudio 오디오 업로드 허용 여부
   * @param allowFile 일반 파일 업로드 허용 여부
   * @returns 비활성화할 확장 기능 목록
   */
  static getDisabledExtensions(
    userExtensions?: string[],
    allowVideo = false,
    allowAudio = false,
    allowFile = false
  ): string[] {
    const set = new Set<string>(userExtensions ?? []);
    if (!allowVideo) set.add("video");
    if (!allowAudio) set.add("audio");
    if (!allowFile) set.add("file");
    return Array.from(set);
  }
}

// 파일 타입 검증 함수
/** @internal 테스트용 export */
export const isImageFile = (file: File, maxSize?: number): boolean => {
  const limit = maxSize !== undefined ? maxSize : MAX_FILE_SIZE;
  if (file.size === 0 || file.size > limit) {
    return false;
  }

  // 🔒 보안: SVG 파일 차단 (XSS 방지)
  const fileName = file.name?.toLowerCase() || "";
  if (
    file.type === "image/svg+xml" ||
    BLOCKED_EXTENSIONS.some((ext) => fileName.endsWith(ext))
  ) {
    return false;
  }

  // 이미지 타입 검증
  return (
    file.type?.startsWith("image/") ||
    (!file.type && /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName))
  );
};

/** @internal 테스트용 export */
export const isVideoFile = (file: File, maxSize?: number): boolean => {
  const limit = maxSize !== undefined ? maxSize : MAX_VIDEO_FILE_SIZE;
  const sizeOk = file.size > 0 && file.size <= limit;
  const fileName = file.name?.toLowerCase() || "";
  const mimeMatch = ALLOWED_VIDEO_MIME_TYPES.has(file.type);
  const videoPrefix = typeof file.type === "string" && file.type.startsWith("video/");
  const extMatch = !file.type && ALLOWED_VIDEO_EXTENSIONS.some((ext) => fileName.endsWith(ext));
  const result = sizeOk && (mimeMatch || videoPrefix || extMatch);
  // #region agent log
  DEBUG_LOG("isVideoFile:check", "result", {
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    sizeOk,
    mimeMatch,
    videoPrefix,
    extMatch,
    result,
  });
  // #endregion
  return result;
};

/**
 * floatingMenu(상단 고정 툴바) 사용 시 팝업 포매팅 툴바를 숨길 선택인지 판별.
 *
 * - 일반 텍스트 선택 → true (상단 툴바가 모든 텍스트 도구를 제공하므로 팝업 중복.
 *   또한 상단 툴바 상호작용 직후 팝업이 잘못된 위치(0,0)로 재표시되는 문제 방지)
 * - 테이블 셀 컨텍스트(CellSelection·셀 내부 커서) → false
 *   (셀 병합·세로 정렬·셀 배경 등 상단 툴바에 없는 도구가 팝업에만 있음)
 * - NodeSelection(이미지/비디오 등) → false (캡션·교체·다운로드 등 파일 도구 유지)
 *
 * @internal 테스트용 export
 */
export const isSuppressiblePopupSelection = (
  editor: any,
  selection: any
): boolean => {
  if (!selection) return false;
  if (selection.node) return false; // NodeSelection
  if (isInTableCell(editor)) return false; // CellSelection 또는 셀 내부 커서
  return true;
};

/** @internal 테스트용 export */
export const isHtmlFile = (file: File): boolean => {
  return (
    file.size > 0 &&
    (file.type === "text/html" ||
      file.name?.toLowerCase().endsWith(".html") ||
      file.name?.toLowerCase().endsWith(".htm"))
  );
};

// ============================================
// 🔒 보안 유틸리티 함수
// ============================================

/**
 * HTML 특수문자 이스케이프 (XSS 방지)
 * URL이나 사용자 입력을 HTML에 삽입할 때 사용
 * @internal 테스트용 export
 */
export const escapeHtml = (str: string): string => {
  const htmlEscapes: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return str.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
};

/**
 * 블록 배열에서 모든 이미지 URL 추출
 * (중첩된 children도 재귀적으로 탐색)
 * @internal 테스트용 export
 */
export const extractImageUrls = (blocks: DefaultPartialBlock[]): Set<string> => {
  const urls = new Set<string>();

  const traverse = (blockList: DefaultPartialBlock[]) => {
    for (const block of blockList) {
      // image 블록에서 URL 추출
      if (block.type === "image" && (block.props as any)?.url) {
        const url = (block.props as any).url;
        if (typeof url === "string" && url.trim()) {
          urls.add(url);
        }
      }
      // children이 있으면 재귀 탐색
      if (block.children && Array.isArray(block.children)) {
        traverse(block.children as DefaultPartialBlock[]);
      }
    }
  };

  traverse(blocks);
  return urls;
};

/**
 * 블록 배열에서 이미지·비디오(미디어) URL 추출
 * (중첩된 children도 재귀적으로 탐색, onImageDelete 삭제 감지용)
 * @internal 테스트용 export
 */
export const extractMediaUrls = (blocks: DefaultPartialBlock[]): Set<string> => {
  const urls = new Set<string>();

  const traverse = (blockList: DefaultPartialBlock[]) => {
    for (const block of blockList) {
      if (block.type === "image" && (block.props as any)?.url) {
        const url = (block.props as any).url;
        if (typeof url === "string" && url.trim()) urls.add(url);
      }
      if (block.type === "video" && (block.props as any)?.url) {
        const url = (block.props as any).url;
        if (typeof url === "string" && url.trim()) urls.add(url);
      }
      if (block.children && Array.isArray(block.children)) {
        traverse(block.children as DefaultPartialBlock[]);
      }
    }
  };

  traverse(blocks);
  return urls;
};

/**
 * 삭제된 미디어(이미지·비디오) URL 찾기
 * (이전 블록에는 있었지만 현재 블록에는 없는 URL)
 * @internal 테스트용 export
 */
export const findDeletedMediaUrls = (
  previousUrls: Set<string>,
  currentUrls: Set<string>
): string[] => {
  const deleted: string[] = [];
  previousUrls.forEach((url) => {
    if (!currentUrls.has(url)) deleted.push(url);
  });
  return deleted;
};

/**
 * 삭제된 이미지 URL 찾기 (findDeletedMediaUrls와 동일 로직, 하위 호환용)
 * @internal 테스트용 export
 */
export const findDeletedImageUrls = (
  previousUrls: Set<string>,
  currentUrls: Set<string>
): string[] => findDeletedMediaUrls(previousUrls, currentUrls);

const findBlockWithLink = (blocks: any[], targetUrl: string): any | null => {
  for (const block of blocks) {
    if (block.content) {
      for (const item of block.content) {
        if (item.type === "link" && item.href === targetUrl) return block;
        if (item.content) {
          for (const sub of item.content) {
            if (sub.type === "link" && sub.href === targetUrl) return block;
          }
        }
      }
    }
    if (block.children?.length) {
      const found = findBlockWithLink(block.children, targetUrl);
      if (found) return found;
    }
  }
  return null;
};

const ConvertToPreviewButton = ({ url }: { url: string }) => {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext()!;

  return (
    <Components.LinkToolbar.Button
      className="bn-button"
      mainTooltip="링크 프리뷰로 전환"
      label="링크 프리뷰로 전환"
      isSelected={false}
      onClick={() => {
        try {
          const allBlocks = (editor as any).document;
          const targetBlock = findBlockWithLink(allBlocks, url)
            || editor.getTextCursorPosition().block;
          (editor as any).replaceBlocks(
            [targetBlock],
            [{ type: "linkPreview", props: { url } }]
          );
        } catch (err) {
          console.error("Convert to link preview failed:", err);
        }
      }}
      icon={
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <line x1="1" y1="9" x2="15" y2="9" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="5" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1" fill="none" />
        </svg>
      }
    />
  );
};

const CustomLinkToolbar = (props: any) => {
  const editor = useBlockNoteEditor();
  const Components = useComponentsContext()!;
  const hasLinkPreview = !!(editor as any)?._linkPreviewApiEndpoint;

  return (
    <Components.LinkToolbar.Root
      className="bn-toolbar bn-link-toolbar"
      onMouseEnter={props.stopHideTimer}
      onMouseLeave={props.startHideTimer}
    >
      <EditLinkButton url={props.url} text={props.text} editLink={props.editLink} />
      <OpenLinkButton url={props.url} />
      <DeleteLinkButton deleteLink={props.deleteLink} />
      {hasLinkPreview && (
        <ConvertToPreviewButton url={props.url} />
      )}
    </Components.LinkToolbar.Root>
  );
};

export default function LumirEditor({
  // editor options
  initialContent,
  initialEmptyBlocks = 3,
  uploadFile,
  s3Upload,
  tables,
  heading,
  defaultStyles = true,
  disableExtensions,
  tabBehavior = "prefer-navigate-ui",
  trailingBlock = true,
  allowVideoUpload = false,
  allowAudioUpload = false,
  allowFileUpload = false,
  maxImageFileSize,
  maxVideoFileSize,
  // link preview
  linkPreview,
  // view options
  editable = true,
  theme = "light",
  formattingToolbar = true,
  linkToolbar = true,
  sideMenu = true,
  emojiPicker = true,
  filePanel = true,
  tableHandles = true,
  onSelectionChange,
  className = "",
  placeholder,
  sideMenuAddButton = false,
  columnDivider = false,
  floatingMenu = false,
  floatingMenuPosition = "sticky",
  // callbacks / refs
  onContentChange,
  onError,
  onImageDelete,
}: LumirEditorProps) {
  // 이미지 업로드 로딩 상태
  const [isUploading, setIsUploading] = useState(false);
  /** S3 업로드 진행률 0–100. null이면 진행률 미표시 */
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  // 에러 상태 (사용자에게 표시할 에러 메시지)
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // FloatingMenu: DOM에 항상 있는 file input (onchange 미발생 방지)
  const floatingMenuFileInputRef = useRef<HTMLInputElement | null>(null);
  const floatingMenuBlockRef = useRef<{ id: string } | null>(null);
  const floatingMenuUploadStartTimeRef = useRef<number>(0);

  // 에러 처리 핸들러
  const handleError = useCallback(
    (error: LumirEditorError) => {
      // 콜백이 있으면 호출
      onError?.(error);
      // 사용자에게 에러 메시지 표시
      setErrorMessage(error.getUserMessage());
      // 3초 후 에러 메시지 자동 숨김
      setTimeout(() => setErrorMessage(null), 3000);
    },
    [onError]
  );
  const validatedContent = useMemo<DefaultPartialBlock[]>(() => {
    // 형제 키 fontSize → styles.fontSize 복원 (직렬화 호환 레이어)
    return liftFontSize(
      ContentUtils.validateContent(initialContent, initialEmptyBlocks)
    );
  }, [initialContent, initialEmptyBlocks]);

  // 테이블 설정 메모이제이션
  const tableConfig = useMemo(() => {
    return EditorConfig.getDefaultTableConfig(tables);
  }, [
    tables?.splitCells,
    tables?.cellBackgroundColor,
    tables?.cellTextColor,
    tables?.headers,
  ]);

  // 헤딩 설정 메모이제이션
  const headingConfig = useMemo(() => {
    return EditorConfig.getDefaultHeadingConfig(heading);
  }, [heading?.levels?.join(",") ?? ""]);

  // 비활성화 확장 메모이제이션
  const disabledExtensions = useMemo(() => {
    return EditorConfig.getDisabledExtensions(
      disableExtensions,
      allowVideoUpload,
      allowAudioUpload,
      allowFileUpload
    );
  }, [disableExtensions, allowVideoUpload, allowAudioUpload, allowFileUpload]);

  // #region agent log
  useEffect(() => {
    DEBUG_LOG("LumirEditor:init:disabledExtensions", "snapshot", {
      allowVideoUpload,
      hasVideoInDisabled: disabledExtensions.includes("video"),
      disabledList: disabledExtensions.slice(0, 15),
    });
  }, [allowVideoUpload, disabledExtensions]);
  // #endregion

  // fileNameTransform 콜백을 ref로 관리 (에디터 재생성 방지)
  const fileNameTransformRef = useRef(s3Upload?.fileNameTransform);
  useEffect(() => {
    fileNameTransformRef.current = s3Upload?.fileNameTransform;
  }, [s3Upload?.fileNameTransform]);

  // S3 업로드 설정 메모이제이션 (객체 참조 안정화)
  // 주의: fileNameTransform은 ref로 관리하므로 의존성에서 제외
  const memoizedS3Upload = useMemo(() => {
    if (!s3Upload) return undefined;
    return {
      apiEndpoint: s3Upload.apiEndpoint,
      env: s3Upload.env,
      path: s3Upload.path,
      appendUUID: s3Upload.appendUUID,
      preserveExtension: s3Upload.preserveExtension,
      uploadTimeoutMs: s3Upload.uploadTimeoutMs,
      maxRetries: s3Upload.maxRetries,
      onProgress: (percent: number) => {
        setUploadProgress(percent);
        s3Upload.onProgress?.(percent);
      },
      // 최신 콜백을 항상 사용하도록 ref를 통해 접근
      fileNameTransform: ((originalName: string, file: File) => {
        return fileNameTransformRef.current
          ? fileNameTransformRef.current(originalName, file)
          : originalName;
      }) as ((originalName: string, file: File) => string) | undefined,
    };
  }, [
    s3Upload?.apiEndpoint,
    s3Upload?.env,
    s3Upload?.path,
    s3Upload?.appendUUID,
    s3Upload?.preserveExtension,
    s3Upload?.uploadTimeoutMs,
    s3Upload?.maxRetries,
    s3Upload?.onProgress,
  ]);

  const editor = useCreateBlockNote(
    {
      // HTML 미리보기 블록이 포함된 커스텀 스키마 사용
      schema,
      // 모든 BlockNote UI 텍스트(테이블 드롭다운 등) 한글 적용 + "색깔"→"색"
      dictionary: {
        ...ko,
        drag_handle: { ...ko.drag_handle, colors_menuitem: "색" },
        formatting_toolbar: {
          ...ko.formatting_toolbar,
          colors: { ...ko.formatting_toolbar.colors, tooltip: "색" },
        },
      },
      initialContent: validatedContent as any,
      tables: tableConfig,
      heading: headingConfig,
      animations: false, // 기본적으로 애니메이션 비활성화
      defaultStyles,
      // 확장 비활성: 비디오/오디오/파일 제어
      disableExtensions: disabledExtensions,
      _tiptapOptions: {
        extensions: [
          VerticalAlignmentExtension,
          // 행 높이 attr 등록은 항상(저장된 높이 렌더), 드래그 리사이즈 UI는
          // tableHandles prop으로 게이트(기존 grip 컨트롤러와 동일 게이트).
          RowHeightExtension.configure({ resizable: tableHandles }),
          // 표 블록 정렬(좌/가운데/우) attr.
          TableAlignmentExtension,
          // 셀 포커스 시 Ctrl/Cmd+A → 표 전체 선택.
          TableSelectAllExtension,
          // blur 상태(툴바 드롭다운 조작 등)에서도 텍스트 선택 하이라이트 유지.
          InactiveSelectionExtension,
        ],
      },
      placeholders: placeholder
        ? { default: placeholder, emptyDocument: placeholder }
        : undefined,
      tabBehavior,
      trailingBlock,
      uploadFile: async (file) => {
        const allowedImage = isImageFile(file, maxImageFileSize);
        const allowedVideo =
          allowVideoUpload && isVideoFile(file, maxVideoFileSize);
        // #region agent log
        DEBUG_LOG("uploadFile:step1:entry", "editor uploadFile callback invoked", {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          allowVideoUpload,
          allowedImage,
          allowedVideo,
        });
        // #endregion
        if (!allowedImage && !allowedVideo) {
          const error = LumirEditorError.invalidFileType(
            file.name,
            allowVideoUpload
          );
          handleError(error);
          throw error;
        }

        try {
          setUploadProgress(0);
          let fileUrl: string;
          // #region agent log
          const branch = uploadFile ? "custom" : memoizedS3Upload?.apiEndpoint ? "s3" : "none";
          DEBUG_LOG("uploadFile:step2:branch", "upload path", {
            branch,
            hasCustomUploadFile: !!uploadFile,
            hasS3ApiEndpoint: !!memoizedS3Upload?.apiEndpoint,
          });
          // #endregion
          // 1. 사용자 정의 uploadFile 우선
          if (uploadFile) {
            const t0 = Date.now();
            DEBUG_LOG("uploadFile:step3a:custom", "calling custom uploadFile", { fileName: file.name });
            fileUrl = await uploadFile(file);
            DEBUG_LOG("uploadFile:step3a:done", "custom uploadFile returned", { urlLen: fileUrl?.length, elapsedMs: Date.now() - t0 });
          }
          // 2. S3 업로드 (uploadFile 없을 때)
          else if (memoizedS3Upload?.apiEndpoint) {
            const t0 = Date.now();
            DEBUG_LOG("uploadFile:step3b:s3", "calling S3 uploader", { fileName: file.name });
            const s3Uploader = createS3Uploader(memoizedS3Upload);
            fileUrl = await s3Uploader(file);
            DEBUG_LOG("uploadFile:step3b:done", "S3 uploader returned", { urlLen: fileUrl?.length, elapsedMs: Date.now() - t0 });
          }
          // 3. 업로드 방법이 없으면 에러
          else {
            const error = LumirEditorError.s3ConfigError(
              "No upload method available. Please provide uploadFile or s3Upload configuration."
            );
            handleError(error);
            throw error;
          }

          // BlockNote가 파일 타입에 따라 이미지/비디오 블록을 생성하도록 URL만 반환
          // #region agent log
          DEBUG_LOG("uploadFile:step4:success", "returning URL", {
            fileName: file.name,
            urlPrefix: fileUrl.slice(0, 80),
          });
          // #endregion
          return fileUrl;
        } catch (error) {
          // #region agent log
          DEBUG_LOG("uploadFile:step5:catch", "uploadFile threw", {
            fileName: file.name,
            errorMessage: error instanceof Error ? error.message : String(error),
          });
          // #endregion
          // 이미 LumirEditorError인 경우 다시 처리하지 않음
          if (error instanceof LumirEditorError) {
            throw error;
          }
          const lumirError = LumirEditorError.uploadFailed(
            error instanceof Error ? error.message : String(error),
            error instanceof Error ? error : undefined
          );
          handleError(lumirError);
          throw lumirError;
        } finally {
          setUploadProgress(null);
        }
      },
      pasteHandler: (ctx) => {
        const { event, editor, defaultPasteHandler } = ctx as any;

        // URL 붙여넣기 감지 → linkPreview 블록 생성
        if (linkPreview?.apiEndpoint) {
          const text = event?.clipboardData?.getData?.("text/plain") || "";
          const trimmed = text.trim();
          if (
            trimmed &&
            /^https?:\/\/\S+$/i.test(trimmed) &&
            !event?.clipboardData?.files?.length
          ) {
            event.preventDefault();
            const currentBlock = editor.getTextCursorPosition().block;
            const blockText = currentBlock.content
              ?.map((c: any) => c.text || "")
              .join("")
              .trim();
            if (!blockText && currentBlock.type === "paragraph") {
              editor.updateBlock(currentBlock, {
                type: "linkPreview",
                props: { url: trimmed },
              });
            } else {
              editor.insertBlocks(
                [{ type: "linkPreview", props: { url: trimmed } }],
                currentBlock,
                "after"
              );
            }
            return true;
          }
        }

        // 엑셀/스프레드시트 표 붙여넣기 감지
        // 엑셀은 클립보드에 표 HTML과 함께 셀 비트맵(image.png)을 올리므로,
        // 아래 이미지 업로드 분기가 가로채기 전에 HTML 표를 우선 처리한다.
        // BlockNote 기본 HTML 파서가 병합/들쑥날쑥 행을 안전하게 표로 변환한다.
        const pastedHtml =
          event?.clipboardData?.getData?.("text/html") || "";
        if (/<table[\s>]/i.test(pastedHtml)) {
          // #region agent log
          DEBUG_LOG("paste:step0:table", "table HTML detected, using pasteHTML", {
            htmlLen: pastedHtml.length,
            hasFiles: !!event?.clipboardData?.files?.length,
          });
          // #endregion
          event.preventDefault();
          // 에디터 너비보다 넓은 표는 비율 유지하며 축소(가로 스크롤 방지):
          // 붙여넣기 전에 HTML에서 열 너비를 읽어 maxWidth에 맞춘 columnWidths를 계산하고,
          // 붙여넣은 뒤 새 표 블록에 적용한다(파서가 셀 width를 colwidth로 읽지 않으므로).
          const pmDom = (editor as any).prosemirrorView?.dom as HTMLElement | undefined;
          const maxWidth = pmDom?.clientWidth ? pmDom.clientWidth - 8 : 0;
          const fittedWidths = computeFittedColumnWidthsPerTable(pastedHtml, maxWidth);
          const beforeTableIds = new Set<string>(
            collectTableBlocks(editor.document).map((b: any) => b.id),
          );
          // 엑셀 CSS 서식(색/정렬/굵게)을 BlockNote가 인식하는 형태로 정규화 후 삽입.
          editor.pasteHTML(normalizeExcelTableHtml(pastedHtml));
          applyFittedWidthsToNewTables(editor, beforeTableIds, fittedWidths);
          return true;
        }

        const fileList =
          (event?.clipboardData?.files as FileList | null) ?? null;
        const files: File[] = fileList ? Array.from(fileList) : [];
        const acceptedFiles: File[] = files.filter(
          (f) =>
            isImageFile(f, maxImageFileSize) || (allowVideoUpload && isVideoFile(f, maxVideoFileSize))
        );
        // #region agent log
        DEBUG_LOG("paste:step1:files", "paste clipboard files", {
          filesCount: files.length,
          acceptedCount: acceptedFiles.length,
          fileNames: files.map((f) => f.name),
          acceptedNames: acceptedFiles.map((f) => f.name),
        });
        // #endregion
        // 파일이 있지만 허용된 미디어가 없으면 기본 처리 막고 무시
        if (files.length > 0 && acceptedFiles.length === 0) {
          event.preventDefault();
          return true;
        }

        if (acceptedFiles.length === 0) {
          return defaultPasteHandler() ?? false;
        }

        event.preventDefault();
        (async () => {
          setIsUploading(true);
          try {
            for (const file of acceptedFiles) {
              try {
                // #region agent log
                DEBUG_LOG("paste:step2:upload", "calling uploadFile for paste", {
                  fileName: file.name,
                  fileType: file.type,
                });
                // #endregion
                const url = await editor.uploadFile(file);
                if (isImageFile(file, maxImageFileSize)) {
                  editor.pasteHTML(
                    `<img src="${escapeHtml(url)}" alt="image" />`
                  );
                } else if (isVideoFile(file, maxVideoFileSize)) {
                  const currentBlock = editor.getTextCursorPosition().block;
                  editor.insertBlocks(
                    [{ type: "video", props: { url } }] as any,
                    currentBlock,
                    "after"
                  );
                }
              } catch (err) {
                console.warn(
                  "Upload failed, skipped:",
                  file.name || "",
                  err
                );
              }
            }
          } finally {
            setIsUploading(false);
          }
        })();
        return true;
      },
    },
    [
      validatedContent,
      tableConfig,
      headingConfig,
      defaultStyles,
      disabledExtensions,
      tabBehavior,
      trailingBlock,
      uploadFile,
      memoizedS3Upload,
      allowVideoUpload,
      linkPreview?.apiEndpoint,
      placeholder,
      // tableHandles 변경 시 RowHeightExtension의 resizable 게이트가 반영되도록 재생성
      tableHandles,
    ]
  );

  // Link Preview API endpoint를 에디터 인스턴스에 동기적으로 연결
  // (useEffect 사용 시 블록 렌더링보다 늦게 설정되어 첫 fetch가 실패할 수 있음)
  if (editor && linkPreview?.apiEndpoint) {
    (editor as any)._linkPreviewApiEndpoint = linkPreview.apiEndpoint;
  }

  // columnDnd 플러그인이 드롭 시 BlockNote 블록 API(getBlock/replaceBlocks)에 접근할 수
  // 있도록, ProseMirror view에 에디터 참조를 심는다(플러그인은 view.__lumirEditor로 읽음).
  if (editor) {
    try {
      const view = (editor as any).prosemirrorView;
      if (view) {
        view.__lumirEditor = editor;
      }
    } catch {
      /* view 미준비 시 무시(다음 렌더에서 설정) */
    }
  }

  // 편집 가능 여부 설정
  useEffect(() => {
    if (editor) {
      editor.isEditable = editable;
    }
  }, [editor, editable]);

  // 행/열 삭제 결정적 처리 래퍼.
  // 코어의 tableHandles.removeRowOrColumn은 삭제 대상 표를 플러그인 내부 상태
  // view.tablePos로 찾는데, 포커스 기반 커스텀 그립 컨트롤러에서는 이 값이 어긋날 수
  // 있어 잘못된 CellSelection이 만들어지고 첫 열/행 삭제가 화면에 반영되지 않는다.
  // 현재 selection(커서)이 위치한 실제 표 기준으로 직접 삭제하고, 표 컨텍스트가
  // 아니면(=false) 코어 기본 동작으로 폴백한다.
  useEffect(() => {
    if (!editor) return;
    const th: any = (editor as any).tableHandles;
    if (!th || th.__lumirColDelPatched || typeof th.removeRowOrColumn !== "function")
      return;
    const orig = th.removeRowOrColumn.bind(th);
    th.removeRowOrColumn = (index: number, dir: "row" | "column") => {
      if (removeFocusedRowOrColumn(editor, index, dir)) return;
      return orig(index, dir);
    };
    th.__lumirColDelPatched = true;
  }, [editor]);

  // floatingMenu(상단 고정 툴바) 사용 시: 일반 텍스트 선택에서는 팝업 포매팅
  // 툴바를 띄우지 않는다 (중복 UI + 상단 툴바에서 스타일 적용 직후 팝업이
  // (0,0) 위치로 잘못 재표시되는 문제 방지). 테이블 셀·노드 선택은 팝업 유지.
  // show:true emit 직후 동기적으로 closeMenu()하면 React 배칭으로 화면에
  // 그려지기 전에 show:false로 정리된다.
  useEffect(() => {
    if (!editor || !floatingMenu) return;

    const ft = (editor as any).formattingToolbar;
    if (!ft?.onUpdate) return;

    const unsubscribe = ft.onUpdate((state: { show?: boolean }) => {
      if (!state?.show) return;
      const selection = (editor as any)._tiptapEditor?.state?.selection;
      if (isSuppressiblePopupSelection(editor, selection)) {
        ft.closeMenu();
      }
    });
    return unsubscribe;
  }, [editor, floatingMenu]);

  // 콘텐츠 변경 감지
  useEffect(() => {
    if (!editor || !onContentChange) return;

    const handleContentChange = () => {
      const blocks = editor.topLevelBlocks as DefaultPartialBlock[];
      // ⚠️ styles.fontSize는 구버전 SDK(fontSize 스펙 없음)를 크래시시키므로
      // 외부로 나가는 JSON은 반드시 lowerFontSize(형제 키 변환)를 거친다.
      // 표 커스텀 셀 attr(세로 정렬·행 높이)은 한 번의 순회로 함께 주입한다.
      const patched = lowerFontSize(
        injectTableBlockAttrs(
          injectTableCellAttrs(blocks, editor, [
            "verticalAlignment",
            "rowHeight",
          ]),
          editor
        )
      );
      onContentChange(patched);
    };

    return editor.onEditorContentChange(handleContentChange);
  }, [editor, onContentChange]);

  // 이미지·비디오 삭제 감지 (onImageDelete 콜백 — 미디어 URL 모두 전달)
  const previousMediaUrlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!editor) return;

    const initialBlocks = editor.topLevelBlocks as DefaultPartialBlock[];
    previousMediaUrlsRef.current = extractMediaUrls(initialBlocks);
  }, [editor]);

  useEffect(() => {
    if (!editor || !onImageDelete) return;

    const handleMediaDeleteCheck = () => {
      const currentBlocks = editor.topLevelBlocks as DefaultPartialBlock[];
      const currentUrls = extractMediaUrls(currentBlocks);
      const previousUrls = previousMediaUrlsRef.current;

      const deletedUrls = findDeletedMediaUrls(previousUrls, currentUrls);
      deletedUrls.forEach((url) => {
        onImageDelete(url);
      });

      previousMediaUrlsRef.current = currentUrls;
    };

    return editor.onEditorContentChange(handleMediaDeleteCheck);
  }, [editor, onImageDelete]);

  // 드래그앤드롭 이미지/HTML 처리
  useEffect(() => {
    const el = editor?.domElement as HTMLElement | undefined;
    if (!el) return;

    const handleDragOver = (e: DragEvent) => {
      if (e.defaultPrevented) return;
      const hasFiles = (
        e.dataTransfer?.types as unknown as string[] | undefined
      )?.includes?.("Files");
      if (hasFiles) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleDrop = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      const hasFiles = (
        (e.dataTransfer.types as unknown as string[] | undefined) ?? []
      ).includes("Files");
      if (!hasFiles) return;

      e.preventDefault();
      e.stopPropagation();

      const items = Array.from(e.dataTransfer.items ?? []);
      const files = items
        .filter((it) => it.kind === "file")
        .map((it) => it.getAsFile())
        .filter((f): f is File => !!f);

      // 이미지, 동영상, HTML 파일 분리
      const imageFiles = files.filter((f) => isImageFile(f, maxImageFileSize));
      const videoFiles = allowVideoUpload
        ? files.filter((f) => isVideoFile(f, maxVideoFileSize))
        : [];
      const htmlFiles = files.filter(isHtmlFile);

      // #region agent log
      DEBUG_LOG("drop:step1:files", "drop received", {
        filesCount: files.length,
        imageCount: imageFiles.length,
        videoCount: videoFiles.length,
        htmlCount: htmlFiles.length,
        allowVideoUpload,
        firstFile: files[0]
          ? {
              name: files[0].name,
              type: files[0].type,
              size: files[0].size,
              isImage: isImageFile(files[0], maxImageFileSize),
              isVideo: isVideoFile(files[0], maxVideoFileSize),
            }
          : null,
      });
      // #endregion

      if (
        imageFiles.length === 0 &&
        htmlFiles.length === 0 &&
        videoFiles.length === 0
      )
        return;

      (async () => {
        setIsUploading(true);
        try {
          // #region agent log
          DEBUG_LOG("drop:step2:async", "drop async started", {
            imageCount: imageFiles.length,
            videoCount: videoFiles.length,
          });
          // #endregion
          // 이미지 파일 처리
          for (const file of imageFiles) {
            try {
              if (editor?.uploadFile) {
                const url = await editor.uploadFile(file);
                if (url && typeof url === "string") {
                  editor.pasteHTML(
                    `<img src="${escapeHtml(url)}" alt="image" />`
                  );
                }
              }
            } catch (err) {
              console.warn(
                "Image upload failed, skipped:",
                file.name || "",
                err
              );
            }
          }

          // 동영상 파일 처리 - video 블록으로 삽입
          // #region agent log
          DEBUG_LOG("drop:step3:videoLoop", "video loop start", {
            videoCount: videoFiles.length,
            names: videoFiles.map((f) => f.name),
          });
          // #endregion
          for (const file of videoFiles) {
            try {
              if (editor?.uploadFile) {
                // #region agent log
                DEBUG_LOG("drop:step4:videoUpload", "calling uploadFile for video", {
                  fileName: file.name,
                });
                // #endregion
                const url = await editor.uploadFile(file);
                if (url && typeof url === "string") {
                  const currentBlock = editor.getTextCursorPosition().block;
                  editor.insertBlocks(
                    [{ type: "video", props: { url } }] as any,
                    currentBlock,
                    "after"
                  );
                }
              }
            } catch (err) {
              console.warn(
                "Video upload failed, skipped:",
                file.name || "",
                err
              );
            }
          }

          // HTML 파일 처리 - htmlPreview 블록으로 삽입
          for (const file of htmlFiles) {
            try {
              const htmlContent = await file.text();
              const currentBlock = editor.getTextCursorPosition().block;

              // htmlPreview 블록 삽입
              editor.insertBlocks(
                [
                  {
                    type: "htmlPreview",
                    props: {
                      htmlContent: htmlContent,
                      fileName: file.name,
                      height: "400px",
                    },
                  },
                ],
                currentBlock,
                "after"
              );
            } catch (err) {
              console.warn(
                "HTML file processing failed, skipped:",
                file.name || "",
                err
              );
            }
          }
        } finally {
          setIsUploading(false);
        }
      })();
    };

    el.addEventListener("dragover", handleDragOver, { capture: true });
    el.addEventListener("drop", handleDrop, { capture: true });

    return () => {
      el.removeEventListener("dragover", handleDragOver, {
        capture: true,
      } as any);
      el.removeEventListener("drop", handleDrop, { capture: true } as any);
    };
  }, [editor, allowVideoUpload]);

  // SideMenu 설정 (Add 버튼 제어)
  const computedSideMenu = useMemo(() => {
    return sideMenuAddButton ? sideMenu : false;
  }, [sideMenuAddButton, sideMenu]);

  // Add 버튼 없는 사이드 메뉴 (드래그 핸들만) - 메모이제이션
  // 드래그 핸들 드롭다운에 표 정렬(좌/가운데/우) 항목을 추가한 커스텀 메뉴 사용.
  const DragHandleOnlySideMenu = useMemo(() => {
    return (props: any) => (
      <BlockSideMenu {...props}>
        <DragHandleButton {...props} dragHandleMenu={LumirDragHandleMenu} />
      </BlockSideMenu>
    );
  }, []);

  return (
    <div
      className={cn(
        "lumirEditor",
        columnDivider && "lumir-column-divider",
        className,
      )}
      style={{ position: "relative", display: "flex", flexDirection: "column" }}
    >
      {/* FloatingMenu를 BlockNoteView 외부로 이동 */}
      {floatingMenu && editor && (
        <>
          <input
            ref={floatingMenuFileInputRef}
            type="file"
            accept={
              allowVideoUpload
                ? "image/*,video/mp4,video/webm,video/ogg,video/quicktime,.mov"
                : "image/*"
            }
            style={{
              position: "absolute",
              left: "-9999px",
              opacity: 0,
              pointerEvents: "none",
            }}
            onChange={async (e) => {
              const inputEl = e.target as HTMLInputElement;
              const file = inputEl.files?.[0];
              // #region agent log
              DEBUG_LOG("FloatingMenu:step3:onchange", "file input onchange fired", {
                hasFile: !!file,
                fileName: file?.name,
                fileType: file?.type,
                fileSize: file?.size,
                hasUploadFile: !!editor?.uploadFile,
              });
              // #endregion
              const blockToInsertAfter = floatingMenuBlockRef.current;
              if (file && editor.uploadFile && blockToInsertAfter) {
                const allowedImage = isImageFile(file, maxImageFileSize);
                const allowedVideo = allowVideoUpload && isVideoFile(file, maxVideoFileSize);
                // #region agent log
                DEBUG_LOG("FloatingMenu:step4:fileCheck", "allowed check", {
                  fileName: file.name,
                  allowedImage,
                  allowedVideo,
                });
                // #endregion
                if (allowedImage || allowedVideo) {
                  try {
                    setIsUploading(true);
                    floatingMenuUploadStartTimeRef.current = Date.now();
                    // #region agent log
                    DEBUG_LOG("FloatingMenu:step5:uploadStart", "calling editor.uploadFile", {
                      fileName: file.name,
                    });
                    // #endregion
                    const url = await editor.uploadFile(file);
                    const blockType = allowedVideo ? "video" : "image";
                    const elapsedMs = Date.now() - floatingMenuUploadStartTimeRef.current;
                    // #region agent log
                    DEBUG_LOG("FloatingMenu:step6:uploadDone", "upload returned, inserting block", {
                      blockType,
                      blockId: blockToInsertAfter.id,
                      urlLen: url?.length,
                      elapsedMs,
                    });
                    // #endregion
                    editor.insertBlocks(
                      [
                        {
                          type: blockType,
                          props: { url: url as string },
                        },
                      ] as any,
                      blockToInsertAfter,
                      "after"
                    );
                  } catch (err) {
                    // #region agent log
                    DEBUG_LOG("FloatingMenu:step7:catch", "upload or insert failed", {
                      errMsg: err instanceof Error ? err.message : String(err),
                    });
                    // #endregion
                    console.error("Upload failed:", err);
                  } finally {
                    setIsUploading(false);
                  }
                }
              }
              inputEl.value = "";
            }}
          />
          <FloatingMenu
            editor={editor as any}
            position={floatingMenuPosition}
            onImageUpload={() => {
              // #region agent log
              DEBUG_LOG("FloatingMenu:step1:click", "upload button clicked", {
                allowVideoUpload,
              });
              // #endregion
              let blockToInsertAfter: { id: string };
              try {
                blockToInsertAfter = editor.getTextCursorPosition().block;
              } catch (err) {
                DEBUG_LOG("FloatingMenu:step1b:error", "getTextCursorPosition failed", {
                  err: err instanceof Error ? err.message : String(err),
                });
                return;
              }
              floatingMenuBlockRef.current = blockToInsertAfter;
              const input = floatingMenuFileInputRef.current;
              if (!input) return;
              input.accept = allowVideoUpload
                ? "image/*,video/mp4,video/webm,video/ogg,video/quicktime,.mov"
                : "image/*";
              input.value = "";
              // #region agent log
              DEBUG_LOG("FloatingMenu:step2:inputReady", "persistent input ref, about to click", {
                accept: input.accept,
              });
              DEBUG_LOG("FloatingMenu:step2b:click", "input.click() about to be called", {});
              // #endregion
              input.click();
            }}
          />
        </>
      )}
      <BlockNoteView
        editor={editor}
        editable={editable}
        theme={theme}
        formattingToolbar={false}
        linkToolbar={false}
        sideMenu={computedSideMenu}
        slashMenu={false}
        emojiPicker={emojiPicker}
        filePanel={filePanel}
        tableHandles={false}
        onSelectionChange={onSelectionChange}
      >
        {/* Notion 스타일 focus 기반 테이블 grip (기본 hover 핸들 대체).
            소비자 tableHandles prop을 게이트로 유지. */}
        {tableHandles && <LumirTableHandlesController />}
        {formattingToolbar && (
          <FormattingToolbarController
            formattingToolbar={CustomFormattingToolbar}
          />
        )}
        {linkToolbar && (
          linkPreview?.apiEndpoint
            ? <LinkToolbarController linkToolbar={CustomLinkToolbar} />
            : <LinkToolbarController />
        )}
        {
          <SuggestionMenuController
            triggerCharacter="/"
            getItems={useCallback(
              async (query: string) => {
                const items = getDefaultReactSlashMenuItems(editor);
                // 오디오/파일 항목 제거; 비디오는 allowVideoUpload일 때만 표시
                const filtered = items.filter((item: any) => {
                  const key = (item?.key || "").toString().toLowerCase();
                  const title = (item?.title || "").toString().toLowerCase();
                  if (key === "video" || title.includes("video"))
                    return allowVideoUpload;
                  if (["audio", "file"].includes(key)) return false;
                  if (title.includes("audio") || title.includes("file"))
                    return false;
                  return true;
                });

                // HTML 미리보기 슬래시 메뉴 항목 추가
                const htmlPreviewItem = {
                  title: "HTML Preview",
                  onItemClick: () => {
                    // 파일 선택 다이얼로그 열기
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = ".html,.htm";
                    input.onchange = async (e) => {
                      const file = (e.target as HTMLInputElement).files?.[0];
                      if (file) {
                        const htmlContent = await file.text();
                        const currentBlock =
                          editor.getTextCursorPosition().block;
                        editor.insertBlocks(
                          [
                            {
                              type: "htmlPreview",
                              props: {
                                htmlContent: htmlContent,
                                fileName: file.name,
                                height: "400px",
                              },
                            },
                          ],
                          currentBlock,
                          "after"
                        );
                      }
                    };
                    input.click();
                  },
                  aliases: ["html", "preview", "웹", "웹페이지"],
                  group: "Embeds",
                  icon: (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="16 18 22 12 16 6"></polyline>
                      <polyline points="8 6 2 12 8 18"></polyline>
                    </svg>
                  ),
                  subtext: "HTML 파일을 미리보기로 삽입",
                };

                // 2단 컬럼(다단 레이아웃) 슬래시 항목 — 생성 시 구분선 유무를 선택해 고정.
                // columnList/column은 public blockSchema에 없어 insertBlocks가 거부 →
                // PM 트랜잭션으로 직접 삽입(insertTwoColumns).
                const columnIcon = (withDivider: boolean) => (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="4" width="7" height="16" rx="1" />
                    <rect x="14" y="4" width="7" height="16" rx="1" />
                    {withDivider && (
                      <line x1="12" y1="3" x2="12" y2="21" strokeDasharray="2 2" />
                    )}
                  </svg>
                );
                const columnItem = {
                  title: "2단 컬럼",
                  onItemClick: () => insertTwoColumns(editor, false),
                  aliases: ["columns", "column", "2col", "단", "컬럼", "다단", "분할"],
                  group: "Basic blocks",
                  icon: columnIcon(false),
                  subtext: "블록을 좌우 2단으로 배치",
                };
                const columnDividerItem = {
                  title: "2단 컬럼 (구분선)",
                  onItemClick: () => insertTwoColumns(editor, true),
                  aliases: [
                    "columns divider",
                    "구분선",
                    "컬럼 구분선",
                    "2단 구분선",
                    "divider",
                  ],
                  group: "Basic blocks",
                  icon: columnIcon(true),
                  subtext: "가운데 세로 구분선이 있는 2단 컬럼",
                };

                const allItems = [
                  ...filtered,
                  htmlPreviewItem,
                  columnItem,
                  columnDividerItem,
                ];

                // Link Preview 슬래시 메뉴 항목 (linkPreview 설정이 있을 때만)
                if (linkPreview?.apiEndpoint) {
                  allItems.push({
                    title: "Link Preview",
                    onItemClick: () => {
                      insertOrUpdateBlock(editor, {
                        type: "linkPreview",
                        props: { url: "" },
                      });
                    },
                    aliases: [
                      "link",
                      "preview",
                      "url",
                      "링크",
                      "미리보기",
                      "프리뷰",
                    ],
                    group: "Embeds",
                    icon: (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    ),
                    subtext: "URL의 미리보기 카드를 삽입",
                  });
                }

                // 한글 제목은 유지하되, 영어로도 검색 가능하도록 각 항목에 영어
                // 로케일(en)의 별칭 + 영어 제목을 별칭으로 병합한다(예: /table, /heading).
                const enSlash = en.slash_menu as Record<
                  string,
                  { title?: string; aliases?: string[] }
                >;
                for (const it of allItems as any[]) {
                  const enEntry = it.key ? enSlash[it.key] : undefined;
                  if (!enEntry) continue;
                  const extra = [...(enEntry.aliases ?? []), enEntry.title]
                    .filter((s): s is string => Boolean(s))
                    .map((s) => s.toLowerCase());
                  it.aliases = Array.from(
                    new Set([...(it.aliases ?? []), ...extra])
                  );
                }

                // 같은 group 항목이 비연속으로 섞여 있으면 SuggestionMenu가 동일
                // group 헤더를 중복 렌더해 React key 충돌이 난다(예: ko 로케일에서
                // "제목" group이 분리됨). group을 안정 정렬해 연속되도록 만든다.
                const groupOrder: string[] = [];
                for (const it of allItems) {
                  const g = (it as any).group ?? "";
                  if (!groupOrder.includes(g)) groupOrder.push(g);
                }
                allItems.sort(
                  (a: any, b: any) =>
                    groupOrder.indexOf(a.group ?? "") -
                    groupOrder.indexOf(b.group ?? "")
                );

                if (!query) return allItems;
                const q = query.toLowerCase();
                return allItems.filter(
                  (item: any) =>
                    item.title?.toLowerCase().includes(q) ||
                    (item.aliases || []).some((a: string) =>
                      a.toLowerCase().includes(q)
                    )
                );
              },
              [editor, allowVideoUpload, linkPreview?.apiEndpoint]
            )}
          />
        }
        {!sideMenuAddButton && (
          <SideMenuController sideMenu={DragHandleOnlySideMenu} />
        )}
      </BlockNoteView>

      {/* 이미지/비디오 업로드 로딩 스피너 */}
      {isUploading && (
        <div className="lumirEditor-upload-overlay">
          <div className="lumirEditor-spinner" />
          {uploadProgress !== null && (
            <span className="lumirEditor-upload-progress">{uploadProgress}%</span>
          )}
        </div>
      )}

      {/* 에러 메시지 토스트 */}
      {errorMessage && (
        <div className="lumirEditor-error-toast">
          <span className="lumirEditor-error-icon">⚠️</span>
          <span className="lumirEditor-error-message">{errorMessage}</span>
          <button
            className="lumirEditor-error-close"
            onClick={() => setErrorMessage(null)}
            type="button"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
